import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../assets/img/ImageIndex";
import axios from "axios";
import RecentPostsSkeleton from "./RecentPostsSkeleton";

const BlogSidebar = ({ recentBlogs: propRecentBlogs, loading: propLoading }) => {
  const [recentBlogs, setRecentBlogs] = useState(propRecentBlogs || []);
  const [loading, setLoading] = useState(propLoading !== undefined ? propLoading : true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Use props if provided, otherwise fetch from API
  const shouldFetchFromAPI = propRecentBlogs === undefined;

  // Sync internal state with props when they change
  useEffect(() => {
    if (propRecentBlogs !== undefined) {
      setRecentBlogs(propRecentBlogs);
    }
  }, [propRecentBlogs]);

  useEffect(() => {
    if (propLoading !== undefined) {
      setLoading(propLoading);
    }
  }, [propLoading]);

  const fetchRecentBlogs = useCallback(async (search = "") => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://cspv0tmvph.execute-api.us-east-1.amazonaws.com/production';
      let apiUrl = `${API_BASE_URL}/blogs?page=1&per_page=4`;

      // Add search parameter if provided
      if (search.trim()) {
        apiUrl += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await axios.get(apiUrl);
      setRecentBlogs(response.data.data.blogs || []);
    } catch (error) {
      console.error('Error fetching recent blogs:', error);
      setRecentBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch on initial mount if no props provided and there's no search term
    if (shouldFetchFromAPI && !debouncedSearchTerm.trim()) {
      fetchRecentBlogs();
    }
  }, [shouldFetchFromAPI, debouncedSearchTerm, fetchRecentBlogs]);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch blogs when debounced search term changes (but not on initial empty search)
  useEffect(() => {
    if (shouldFetchFromAPI && debouncedSearchTerm.trim()) {
      fetchRecentBlogs(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, fetchRecentBlogs, shouldFetchFromAPI]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Only search if we're fetching from API
    if (shouldFetchFromAPI) {
      fetchRecentBlogs(searchTerm);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderBlogItem = (blog) => {
    return (
      <article key={blog.blog_id} className="item post-641 post type-post status-publish format-standard has-post-thumbnail hentry">
        <Link to={`/blog-details/${blog.slug}`} className="thumb hover-effect" aria-label={blog.title}>
          <span
            className="fullimage cover"
            role="img"
            aria-label={blog.title}
            style={{
              background: blog.image_path
                ? `url(${blog.image_path})`
                : `url(${IMAGES.imageNotFound})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></span>
        </Link>
        <div className="info gradient-effect">
          <div className="d-flex align-items-center">
            <time dateTime={blog.created_at}>{formatDate(blog.created_at)}</time>
          </div>
          <h4 className="title usmall">
            <Link to={`/blog-details/${blog.slug}`}>{blog.title}</Link>
          </h4>
        </div>
        <div className="clear"></div>
      </article>
    );
  };

  return (
    <div id="secondary" className="title blog-sidebar">
      <div id="search-2" className="widget widget_search">
        <form role="search" method="get" onSubmit={handleSearch} className="search-form">
          <label>
            <input
              type="search"
              className="search-field"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              name="search"
              required=""
            />
          </label>
          <button type="submit" className="search-submit">
            <i className="ri-search-2-line"></i>
          </button>
        </form>
      </div>

      <div id="ralo_posts_thumbs-2" className="widget widget_ralo_posts_thumbs">
        <h3 className="widget-title">Recent posts</h3>
        {loading ? (
          <RecentPostsSkeleton />
        ) : recentBlogs.length > 0 ? (
          recentBlogs.map(blog => renderBlogItem(blog))
        ) : (
          <p className="text-muted">No recent posts found.</p>
        )}
      </div>

      {/* <div id="categories-2" className="widget widget_categories">
        <h3 className="widget-title">Categories</h3>
        <ul>
          <li className="cat-item cat-item-3">
            <Link >Blog</Link>
          </li>
        </ul>
      </div>

      <div id="tag_cloud-2" className="widget widget_tag_cloud">
        <h3 className="widget-title">Tags</h3>
        <div className="tagcloud">
          <Link className="tag-cloud-link tag-link-6 tag-link-position-1" style={{ fontSize: '22pt' }} aria-label="Blog">Blog</Link>
        </div>
      </div> */}
    </div>
  );
};

export default BlogSidebar;
